import { useSyncExternalStore } from 'react';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  available: boolean;
  installed: boolean;
}

let deferredPrompt: InstallPromptEvent | null = null;
let state: InstallState = { available: false, installed: false };
const listeners = new Set<() => void>();
let initialized = false;

function publish(next: InstallState) {
  state = next;
  listeners.forEach((listener) => listener());
}

function setPreference(name: string) {
  // Only a UI preference is shared with the landing; no account data.
  const domain = /(^|\.)lomora\.ru$/.test(location.hostname) ? '; Domain=lomora.ru' : '';
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  try {
    document.cookie = `${name}=1; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
  } catch {
    // Blocking preference storage must not block installation or navigation.
  }
}

function hasPreference(name: string): boolean {
  try {
    return document.cookie.split(';').some((entry) => entry.trim() === `${name}=1`);
  } catch {
    return false;
  }
}

/** Приложение уже ставили на этом устройстве (cookie общий с лендингом). Если его удалили,
 *  браузер снова присылает beforeinstallprompt — тогда установка снова доступна. */
export function installedBefore(): boolean {
  return hasPreference('lomora.pwa-installed');
}

export function dismissInstallPromotion() {
  setPreference('lomora.pwa-prompt-dismissed');
}

export function initializePwaInstall() {
  if (initialized) return;
  initialized = true;
  const standalone = window.matchMedia('(display-mode: standalone)');
  const markInstalled = () => {
    deferredPrompt = null;
    setPreference('lomora.pwa-installed');
    publish({ available: false, installed: true });
  };
  if (standalone.matches || (navigator as Navigator & { standalone?: boolean }).standalone) {
    markInstalled();
  }
  standalone.addEventListener('change', () => {
    if (standalone.matches) markInstalled();
  });
  window.addEventListener('appinstalled', markInstalled);
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    // An actual install event also handles an app that was uninstalled.
    publish({ available: true, installed: false });
  });
}

export function usePwaInstall() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

export async function promptPwaInstall(): Promise<'accepted' | 'dismissed' | 'unavailable' | 'error'> {
  const prompt = deferredPrompt;
  if (!prompt) return 'unavailable';
  // A beforeinstallprompt event can only be used once, including on dismissal.
  deferredPrompt = null;
  publish({ ...state, available: false });
  try {
    await prompt.prompt();
    return (await prompt.userChoice).outcome;
  } catch {
    return 'error';
  }
}

export type InstallPlatform = 'ios' | 'android' | 'desktop';

export function getInstallPlatform(): InstallPlatform {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}
